import "./customer-card.scss";

import { GetUserLevelModel } from "@factory/model/query-model-quota-system";
import { RewardBalance } from "@factory/model/query-model-reward";
import { ScanMethod } from "@factory/model/scan-method.enum";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import classNames from "classnames";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { useState } from "react";
import Barcode from "react-barcode";
import QRCode from "react-qr-code";

import { appFeature } from "../../../app.feature";
import AnimatedCounter from "../animated-counter/animated-counter";
import CustomerLevel from "../customer-level/customer-level";

interface CustomerCardProps {
  userLevel?: GetUserLevelModel;
  balanceData?: RewardBalance;
  avatar?: string;
  placement?: string;
  hidden?: boolean;
  disableButton?: boolean;
  shouldShowCard?: boolean;
  onClickFn?: () => void;
  onChangeDisplay?: (display: ScanMethod) => void;
}

export function CustomerCard({
  userLevel,
  balanceData,
  avatar,
  placement,
  hidden,
  disableButton,
  shouldShowCard,
  onClickFn = () => { },
  onChangeDisplay = () => { },
}: CustomerCardProps) {
  const [displayMode, setDisplayMode] = useState<string>(ScanMethod.QR);
  const points = balanceData?.current_points ?? 0;
  const stamps = parseInt(balanceData?.current_stamp?.toString() ?? "0") ?? 0;
  const { formatDate } = useDateTimeContext();
  const { pointsText, stampsText } = appFeature.config.label;
  const [showMode, setShowMode] = useState<string>("PROFILE");

  const toggleDisplay = () => {
    switch (displayMode) {
      case ScanMethod.QR:
        onChangeDisplay(ScanMethod.Barcode);
        setDisplayMode(ScanMethod.Barcode);
        break;
      case ScanMethod.Barcode:
        onChangeDisplay(ScanMethod.QR);
        setDisplayMode(ScanMethod.QR);
        break;
      default:
        onChangeDisplay(ScanMethod.QR);
        setDisplayMode(ScanMethod.QR);
        break;
    }
  };

  if (!balanceData) {
    return (
      <button
        className={` ${hidden ? "bg-none" : "bg-gray-200"}   w-[20%] h-[30vh] ${placement === "left" ? "rounded-r-lg" : "rounded-l-lg"
          } flex items-center justify-center`}
        disabled={disableButton}
        onClick={() => {
          onClickFn();
        }}
      ></button>
    );
  }
  return (
    <div
      id="CustomerCard"
      className={`customer-card bg-white shadow-md w-full md:w-3/4 rounded-lg p-5`}
    >
      <div className="flex justify-center mb-2">
        <Button
          label="Profile"
          className={classNames(
            showMode === "PROFILE" ? "bg-gray-300" : "bg-gray-200",
            "outline-none shadow-none text-gray-600 border-r-1 border-l-0 border-y-0 border-gray-500 hover:bg-gray-300 rounded-r-none rounded-l-full",
          )}
          onClick={() => setShowMode("PROFILE")}
        />
        <Button
          label="Code"
          className={classNames(
            showMode === "CODE" ? "bg-gray-300" : "bg-gray-200",
            "outline-none shadow-none text-gray-600 border-none hover:bg-gray-300 rounded-l-none rounded-r-full",
          )}
          onClick={() => setShowMode("CODE")}
        />
      </div>
      <div className={classNames("flex justify-center")}>
        {showMode === "PROFILE" && (
          <div
            className={classNames(
              "justify-center w-full",
            )}
          >
            <div className={classNames("mb-4", userLevel ? ("md:grid-cols-2") : (""))}>
              {balanceData ? (
                <CustomerLevel avatar={avatar} balance={balanceData} userLevel={userLevel} />
              ) : null}
            </div>
            <div>
              <div className="flex justify-center gap-4">
                <AnimatedCounter
                  from={0}
                  to={points}
                  duration={1}
                  delay={1}
                  label={pointsText}
                />
                <Divider layout="vertical" />
                <AnimatedCounter
                  from={0}
                  to={stamps}
                  duration={1}
                  delay={1}
                  label={stampsText}
                />
              </div>
              <p className="text-xs mt-4 w-full text-gray-600">
                Card Expiration: {formatDate(balanceData?.expiration_date)}
              </p>
            </div>
          </div>
        )}
        {showMode === "CODE" && shouldShowCard && (
          <div
            className={classNames(
              "flex flex-col",
              showMode === "CODE" ? "" : "hidden",
            )}
          >
            <div className="flex justify-end pr-2 pb-2">
              <button className="pi pi-sync" onClick={toggleDisplay}></button>
            </div>
            <div className="flex justify-center bg-white md:min-h-60">
              {displayMode === "qr" && (
                <section>
                  <QRCode value={balanceData?.card_alias || ""} size={200} />
                  <p className="text-xs font-semibold text-center mt-2">
                    {balanceData.card_alias}
                  </p>
                </section>
              )}
              {displayMode === "barcode" && (
                <Barcode value={balanceData?.card_alias || ""} width={1.6} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default CustomerCard;
